import React, { useEffect, useState } from "react"
import { useUser } from "../../api/users/store"
import Loading from "../../components/Loading"
import CStack from "../../components/CStack"
import { AppRoleDef, updateRoles, UserInfo, removeUserCompanyAccess } from "../../api/users/actions"
import CAccordion from "../../components/CAccordion"
import { Col, Pagination, Row, Form, Button, Stack } from "react-bootstrap"
import InviteDialog from "../../containers/InviteDialog"
import { ADMIN_ROLE, CORE1_COM, GLOBAL_APP, OWNER_ROLE, SUPPORT_ROLE, USER_BLOCKED_STATUS_STRING, USER_STATUS_AWAITING_RESET_STRING, USERS_PAGE_SIZE } from "../../constants" // Import status constants
import { useReg } from "../../api/reg/store"
import { useAuth } from "../../api/auth/store"
import { AppRole } from "../../api/reg/actions"

import useUXStore from "../../api/ux/store"
import BranchAssign from "../../containers/Users/BranchAssign"
import { useBranch } from "../../api/branch/store"
import EditableControl, { EditableLabel, EditableName } from "../../components/EditableControl"
import RoleAssign from "../../containers/Users/RoleAssign"
import ButtonControl from "../../components/ButtonControl" // Import ButtonControl
import { useApps } from "../../api/apps/store"
import UserSearchFilter from "../../components/UserSearchFilter"
import { ReactComponent as DeleteIcon } from "../../svgs/delete.svg"
import { useCompanies } from "../../api/companies/store"
import useForceDeleteUser from "../../hooks/useForceDeleteUser"
import CompanyCrossBrowse from "./CompanyCrossBrowse"

const defName = { lname: "", fname: "" }

type UserItemProps = {
    user: UserInfo | null
    onResend: (role: AppRoleDef) => void
    onDelete: (role: AppRoleDef) => void
    // Set while viewing another company's users. Every write endpoint takes its
    // company from the session, so editing here would silently modify core1 instead.
    readOnly?: boolean
    // Present only for core1 admins; omitted elsewhere so the action never renders.
    onForceDelete?: (email: string) => void
}

const UserItem: React.FC<UserItemProps> = ({ user, onResend, onDelete, readOnly, onForceDelete }) => {
    const { hasUserRole, updateUserPref, adminInitiatePasswordReset } = useUser(state => state) // Get new store action
    const { hasRole, currentCompany, sessionDetails } = useAuth(state => state)
    const { retrieveUserBranches, userBranches } = useBranch(state => state)
    const { addToast } = useUXStore(state => state)
    const { refreshDialog } = useUXStore(state => state)
    const [editable, setEditable] = useState(true)
    const [expanded, setExpanded] = useState(false)

    //console.log(user)

    // disable control if this is owner
    let disableEdit = hasUserRole(user!.email, GLOBAL_APP, [OWNER_ROLE])
    // admin role will only be editable by owner
    
    // remove this code temporarily to allow admin to edit other admin users
    /*if (!disableEdit) {
        if (hasUserRole(user!.email, GLOBAL_APP, [ADMIN_ROLE])) {
            // is logged user has role
            if (!hasRole(OWNER_ROLE)) {
                disableEdit = true
            }
        }
    }*/
    const onExpand = (appear: boolean) => {
        setExpanded(appear)
        if (appear) {
            retrieveUserBranches(currentCompany, user!.userid!, 0)
            setEditable(true)
        }
        else
            setEditable(false)
    }
    const onChangedInvitePref = (data: any) => {
        updateUserPref(data.lname, data.fname, user?.userid!)
    }

    const handleInitiateReset = async () => {
        if (user?.userid) {
            await adminInitiatePasswordReset(user.username!);
        }
    }

    const onDeleteUser = () => {
        refreshDialog({
            id: "deleteUserAccess",
            title: "Remove User Access",
            visible: true,
            content: (
                <div>
                    <p>Are you sure you want to remove <b>{user?.email}</b>'s access to this company?</p>
                    <div style={{ marginTop: 10, color: "orange" }}>
                        <strong>⚠️ Warning: This action will:</strong>
                        <ul>
                            <li>Remove all app access for this company</li>
                            <li>Terminate all active sessions</li>
                            <li>Remove branch assignments</li>
                            <li>Delete company preferences</li>
                        </ul>
                    </div>
                </div>
            ),
            confirmContent: "Remove Access",
            onConfirm: () => {
                const { retrieveAllUsers } = useUser.getState();
                const { currentPage, searchQuery, filters } = useUser.getState();

                try {
                    removeUserCompanyAccess(user!.email).then(_ => {
                        addToast("", "User access removed successfully.");
                        // Refresh the user list
                        retrieveAllUsers(currentPage, searchQuery, filters);
                    });

                } catch (error) {
                    addToast("Failed", "Failed to remove user access.");
                    console.error(error);
                }
            }
        }, true);
    }

    let name = user?.email
    const pref = user?.pref
    if (pref && pref.lname + pref.fname !== "")
        name = pref.fname + " " + pref.lname
    const hasEditAccess = (hasRole(OWNER_ROLE) || hasRole(ADMIN_ROLE)) && !hasRole(SUPPORT_ROLE) && !readOnly
    // TODO: editable control should only call apply when it is dirty
    // User owner shouldnt be removable
    return <CAccordion labelColor={user?.status === USER_BLOCKED_STATUS_STRING ? "red" : ""} label={name} onExpand={onExpand}>
        {expanded && user && ( // Ensure user is not null
            <Col>
                <EditableControl editable={editable && hasEditAccess} defaultData={(user.pref ? user.pref : defName)} onApply={onChangedInvitePref} className="">
                    {(data, onChanged, edit) => (<>
                        {(edit || !(!data.lname || !data.fname)) && <EditableName md="auto" label="Name" lname={data.lname} fname={data.fname} edit={edit}
                            onChangedFname={(fname) => onChanged("fname", fname)}
                            onChangedLname={(lname) => onChanged("lname", lname)}
                        />}
                        {!edit && (!data.lname || !data.fname) && <EditableLabel md="auto" label="Name" value="Default" />}
                        <EditableLabel md="auto" label="Email" value={user.email} />
                        <br />

                        <BranchAssign edit={edit} assigned={userBranches[user.userid!]!} userid={user.userid!} />
                        <RoleAssign user={user} edit={edit && !disableEdit} />

                        {/* Delete User Button - show only in edit mode for non-owner users and not self */}
                        {edit && hasEditAccess && !hasUserRole(user.email, GLOBAL_APP, [OWNER_ROLE]) && user.userid !== sessionDetails?.uid && (
                            <div style={{ marginTop: 20 }}>
                                <hr style={{ margin: "10px 0" }} />
                                <Button size="sm" variant="link" onClick={onDeleteUser} style={{ padding: 0 }}>
                                    <DeleteIcon stroke="red" style={{ width: 25, height: 25 }} />
                                    <span style={{ color: "red", marginLeft: 5 }}>Remove User Access</span>
                                </Button>
                            </div>
                        )}

                        {/* Force delete: core1 only, and never against yourself. Shown
                            outside the edit gate so it stays available while browsing
                            another company read-only. */}
                        {onForceDelete && user.userid !== sessionDetails?.uid && (
                            <div style={{ marginTop: 20 }}>
                                <hr style={{ margin: "10px 0" }} />
                                <Button size="sm" variant="link" onClick={() => onForceDelete(user.email)} style={{ padding: 0 }}>
                                    <DeleteIcon stroke="red" style={{ width: 25, height: 25 }} />
                                    <span style={{ color: "red", marginLeft: 5 }}>Force Delete User</span>
                                </Button>
                                <div style={{ color: "#565656", fontSize: 12, marginTop: 4 }}>
                                    Permanently erases this user and all their data from every company.
                                </div>
                            </div>
                        )}

                        {/* Add Initiate Password Reset Button */}
                        {hasEditAccess && (user.status === USER_BLOCKED_STATUS_STRING || user.status === USER_STATUS_AWAITING_RESET_STRING) && (
                            <div style={{ marginTop: 30 }}>
                                <ButtonControl
                                    text={"Account Blocked "}
                                    labelColor="red"
                                    caption={(user.status === USER_STATUS_AWAITING_RESET_STRING ? "Reset sent" : "")}
                                    buttonLabel="Reset" // Corrected prop name
                                    onClicked={handleInitiateReset} // Corrected prop name
                                // Add styling/variant as needed
                                />
                            </div>
                        )}
                    </>)}
                </EditableControl>
            </Col>
        )}
    </CAccordion>
}

const Users = () => {
    const { loading, retrieveAllUsers, users, roles, addUser, totalUsers, currentPage, setCurrentPage, searchQuery, setSearchQuery, filters, setFilters } = useUser(state => state)
    const { signed, currentCompany, hasRole, profile } = useAuth(state => state)
    const { invite } = useReg(state => state)
    const { addToast } = useUXStore(state => state)
    const { retrieveBranches, branches } = useBranch(state => state)
    const { retrieveApps, apps } = useApps(state => state)
    const { companies, retrieveCompanies } = useCompanies(state => state)
    const forceDeleteFlow = useForceDeleteUser()

    // Core1 privileges. The server enforces all of this again on every request; these
    // checks only decide what is worth rendering.
    const isCore1 = currentCompany === CORE1_COM
    const isPrivileged = (hasRole(OWNER_ROLE) || hasRole(ADMIN_ROLE)) && !hasRole(SUPPORT_ROLE)
    const canManageCrossCompanyBrowsing = isCore1 && hasRole(OWNER_ROLE)
    const canForceDelete = isCore1 && isPrivileged
    const crossComEnabled = isCore1 && isPrivileged && !!profile?.cross_com_browse
    const browsingForeign = !!filters.com && filters.com !== currentCompany

    useEffect(() => {
        if (signed) {
            retrieveAllUsers(currentPage, searchQuery, filters)
            retrieveBranches(currentCompany)
            retrieveApps()
        }
    }, [signed, currentPage, searchQuery, filters, retrieveAllUsers, retrieveBranches, retrieveApps, currentCompany])

    useEffect(() => {
        if (signed && crossComEnabled && !companies) {
            retrieveCompanies()
        }
    }, [signed, crossComEnabled, companies, retrieveCompanies])

    // Recover from a stale page number, e.g. when a removal or a filter change leaves
    // fewer pages than the page currently being viewed
    useEffect(() => {
        const lastPage = Math.ceil(totalUsers / USERS_PAGE_SIZE)
        if (totalUsers > 0 && currentPage > lastPage) {
            setCurrentPage(lastPage)
        }
    }, [totalUsers, currentPage, setCurrentPage])

    if (!roles || !users) {
        return <Loading center />
    }
    const onResendInvite = (user: UserInfo, appDef: AppRoleDef) => {
        //console.log(appDef)
        let roles: string[] = []
        roles = appDef.roles?.map(item => item.id)!
        //console.log(roles)
        invite(user.email, [{
            app: appDef.id,
            roles: roles
        }]).then(code => {
            switch (code) {
                case "200":
                    addToast("", "Invite resent!")
                    break;
                default:
                    // TODO: add toast
                    break;
            }
        })
    }
    const onDeleteInvite = (email: string, appDef: AppRoleDef) => {
        updateRoles(email, [{ app: appDef.id, roles: [] }])
            .then(code => {
                switch (code) {
                    case "200":
                        addToast("", appDef.id + " invite deleted successfully.")
                        break;

                    default:
                        addToast("Failed", "Delete invite failed.")
                        break;
                }
            })
    }
    const onInvited = (emails: string[], roles: AppRole[]) => {
        console.log(roles)
        emails.forEach(email => addUser(email, roles as any))
    }

    // Pagination calculations
    const pageSize = USERS_PAGE_SIZE
    const totalPages = Math.ceil(totalUsers / pageSize)
    const startRange = totalUsers === 0 ? 0 : (currentPage - 1) * pageSize + 1
    const endRange = Math.min(currentPage * pageSize, totalUsers)

    // Generate page numbers with ellipsis
    const generatePageNumbers = (): (number | string)[] => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1)
        }

        const pages: (number | string)[] = []

        // Always show first page
        pages.push(1)

        // Show ellipsis or page 2
        if (currentPage > 3) {
            pages.push('...')
        } else if (currentPage > 1) {
            pages.push(2)
        }

        // Show current and adjacent pages
        for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
            if (!pages.includes(i)) {
                pages.push(i)
            }
        }

        // Show ellipsis or page totalPages-1
        if (currentPage < totalPages - 2) {
            pages.push('...')
        } else if (currentPage < totalPages && totalPages > 2) {
            pages.push(totalPages - 1)
        }

        // Always show last page
        if (totalPages > 1) {
            pages.push(totalPages)
        }

        return pages
    }

    const handlePageChange = (page: number) => {
        if (page !== currentPage && page >= 1 && page <= totalPages) {
            setCurrentPage(page)
        }
    }

    // Search and filter handlers
    const handleSearchChange = (query: string) => {
        setSearchQuery(query)
        setCurrentPage(1)
    }

    const handleFiltersChange = (newFilters: { branch?: string; app?: string; com?: string }) => {
        setFilters(newFilters)
        setCurrentPage(1)
    }

    const handleClearFilters = () => {
        setSearchQuery("")
        setFilters({})
        setCurrentPage(1)
    }

    const canInvite = !hasRole(SUPPORT_ROLE)

    return <>
        {forceDeleteFlow.node}
        {canInvite && <InviteDialog onInvited={onInvited} />}
        <CStack header="Users">
            {canManageCrossCompanyBrowsing && <CompanyCrossBrowse />}
            <UserSearchFilter
                searchQuery={searchQuery}
                filters={filters}
                branches={browsingForeign ? null : (branches && branches[currentCompany] ? branches[currentCompany] : null)}
                apps={apps ? apps.filter(a => a.id && a.name).map(a => ({ id: a.id!, name: a.name! })) : null}
                companies={crossComEnabled ? companies : null}
                onSearchChange={handleSearchChange}
                onFiltersChange={handleFiltersChange}
                onClearFilters={handleClearFilters}
            />
            {browsingForeign && (
                <div style={{ padding: "8px 12px", marginBottom: "1rem", borderRadius: 4, background: "#fff4e5", color: "#663c00" }}>
                    Viewing users of <b>{companies?.find(c => c.id === filters.com)?.name || filters.com}</b>. This
                    view is read-only.
                </div>
            )}
            {users && Object.keys(users!).length > 0 ? (
                Object.keys(users!).map((val, i) =>
                    <UserItem key={i + "user"}
                        user={users[val]}
                        readOnly={browsingForeign}
                        onForceDelete={canForceDelete ? forceDeleteFlow.start : undefined}
                        onDelete={(_roles) => onDeleteInvite(users[val]!.email, _roles)}
                        onResend={(roleDef) => onResendInvite(users![val]!, roleDef)} />)
            ) : (
                <div style={{ textAlign: "center", padding: "40px", color: "#565656" }}>
                    No users found
                </div>
            )}
        </CStack>
        {loading && <Loading center />}
        {!loading && totalUsers > pageSize && (
            <div style={{ padding: "15px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ color: "#565656" }}>
                    Showing {startRange}-{endRange} of {totalUsers} users
                </div>
                <Pagination>
                    <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {generatePageNumbers().map((page, index) => {
                        if (page === '...') {
                            return <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                        }
                        return (
                            <Pagination.Item
                                key={page}
                                active={page === currentPage}
                                onClick={() => handlePageChange(page as number)}
                            >
                                {page}
                            </Pagination.Item>
                        )
                    })}
                    <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                    />
                </Pagination>
            </div>
        )}
    </>
}

export default Users
